import React, { useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Check, Eye, EyeOff, Loader, LogIn, Mail, Sun, Moon } from 'lucide-react';
import { AppLogoMark } from '../LogoEditor';
import { Icon } from './Icon';
import Button from './ui/Button';
import { Z } from '../constants/tokens';

export default function LoginModal({ BACKEND_URL, googleClientId, theme, onLogin, isDarkMode, isDevEnvironment, onToggleDark }) {
  const cardWidth = 380;
  const cardPadding = 32;
  const brandWidth = cardWidth - (cardPadding * 2);
  const [mode, setMode]           = useState('choose');  // choose | local | google | request
  const [requestEmail,   setRequestEmail]   = useState('');
  const [requestName,    setRequestName]    = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [requestSent,    setRequestSent]    = useState(false);
  const [sending,        setSending]        = useState(false);
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [showPw, setShowPw]       = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const googleBtnRef              = React.useRef(null);

  // Load Google Identity Services script
  React.useEffect(() => {
    if (mode !== 'google') return;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (!window.google) return;
      const initGoogle = (clientId) => {
        if (!clientId) { setError('Google Client ID not configured on server'); return; }
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async ({ credential }) => {
            setLoading(true); setError('');
            try {
              const res = await axios.post(`${BACKEND_URL}/auth/google`, { id_token: credential });
              onLogin(res.data.token, res.data.user);
            } catch (e) {
              setError(e?.response?.data?.detail || 'Google sign-in failed');
            } finally { setLoading(false); }
          },
        });
        window.google.accounts.id.renderButton(
          googleBtnRef.current,
          { theme: isDarkMode ? 'filled_black' : 'outline', size: 'large', width: brandWidth, text: 'signin_with' }
        );
        window.google.accounts.id.prompt();
      };

      if (googleClientId) {
        initGoogle(googleClientId);
      } else {
        axios.get(`${BACKEND_URL}/auth/config`)
          .then(r => {
            const cid = r.data?.google_client_id;
            if (cid) initGoogle(cid);
            else setError('Google Client ID not configured. Set it in Admin → API Keys.');
          })
          .catch(() => setError('Could not load Google Client ID from server. Backend may still be starting.'));
      }
    };
    document.head.appendChild(script);
    return () => { try { document.head.removeChild(script); } catch {} };
  }, [mode, googleClientId, BACKEND_URL, isDarkMode, onLogin]); // eslint-disable-line

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${BACKEND_URL}/auth/login`, { email, password });
      onLogin(res.data.token, res.data.user);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const sendRequest = async () => {
    setSending(true); setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/auth/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: requestEmail, name: requestName, message: requestMessage })
      });
      if (!res.ok) {
        let detail = 'Could not send your request. Please try again.';
        try { const d = await res.json(); if (d?.detail) detail = d.detail; } catch {}
        setError(detail);
        return;
      }
      setRequestSent(true);
    } catch {
      setError('Could not send your request. Please try again.');
    } finally { setSending(false); }
  };

  const inputStyle = {
    width: '100%', padding: '11px 14px', borderRadius: '8px', fontSize: '0.9rem',
    background: theme.inputBg || (isDarkMode ? '#000' : '#fff'),
    color: theme.text, border: `1px solid ${theme.border}`,
    outline: 'none', boxSizing: 'border-box',
  };

  const applyFocusRing = (e) => {
    e.currentTarget.style.borderColor = theme.glossaryAccent || '#007aff';
    e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.glossaryAccent || '#007aff'}40`;
  };
  const removeFocusRing = (e) => {
    e.currentTarget.style.borderColor = theme.border;
    e.currentTarget.style.boxShadow = 'none';
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: Z.modal, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: isDarkMode ? '#0a0a0f' : '#f5f5f7' }}>

      {/* Dark/light toggle — so users can pick their theme before signing in. */}
      {onToggleDark && (
        <button
          type="button"
          onClick={onToggleDark}
          title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{ position: 'absolute', top: '20px', right: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '999px', background: theme.card, border: `1px solid ${theme.border}`, color: theme.text, cursor: 'pointer' }}
        >
          <Icon icon={isDarkMode ? Sun : Moon} size={18} />
        </button>
      )}

      {/* Page branding above the card */}
      <div style={{ width: `${brandWidth}px`, maxWidth: '90vw', marginBottom: '32px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
        <AppLogoMark fallbackSize={62} />
        <div style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '2rem', lineHeight: 1, fontWeight: 500, letterSpacing: '0.015em', color: theme.text, whiteSpace: 'nowrap' }}>
          Giving Words to Voices™
        </div>
        <div style={{ fontSize: '0.82rem', color: theme.textSub, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600 }}>
          Archive Transcription Platform
        </div>
      </div>

      <div style={{ width: `${cardWidth}px`, maxWidth: '90vw', background: theme.sidebar, border: `1px solid ${theme.border}`, borderRadius: '16px', padding: `${cardPadding}px`, boxShadow: isDarkMode ? '0 24px 80px rgba(0,0,0,0.6)' : '0 24px 80px rgba(0,0,0,0.12)' }}>

        {error && (
          <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '0.82rem', color: '#b91c1c', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <Icon icon={AlertTriangle} size={14} style={{ flexShrink: 0, marginTop: '1px' }} />{error}
          </div>
        )}

        {/* Choose method */}
        {mode === 'choose' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button onClick={() => setMode('google')}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '13px', borderRadius: '10px', border: `1.5px solid ${theme.border}`, background: theme.card, color: theme.text, fontWeight: 'bold', fontSize: '0.9rem', cursor: 'pointer', transition: '0.15s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#4285f4'}
              onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}
            >
              {/* Google coloured G */}
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '4px 0' }}>
              <div style={{ flex: 1, height: '1px', background: theme.border }} />
              <span style={{ fontSize: '0.75rem', color: theme.textSub }}>or</span>
              <div style={{ flex: 1, height: '1px', background: theme.border }} />
            </div>

            <Button theme={theme} variant="secondary" size="lg" fullWidth icon={Mail} onClick={() => setMode('local')}>
              Sign in with Email
            </Button>
          </div>
        )}

        {/* Local login form */}
        {mode === 'local' && (
          <form onSubmit={handleLocalLogin} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 'bold', color: theme.textSub, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com" style={inputStyle} autoFocus onFocus={applyFocusRing} onBlur={removeFocusRing} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 'bold', color: theme.textSub, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Password</label>
              <div style={{ position: 'relative' }}>
                <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required placeholder="••••••••" style={{ ...inputStyle, paddingRight: '42px' }} onFocus={applyFocusRing} onBlur={removeFocusRing} />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  aria-label={showPw ? 'Hide password' : 'Show password'} aria-pressed={showPw} title={showPw ? 'Hide password' : 'Show password'}
                  style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: theme.textSub, display: 'flex' }}>
                  <Icon icon={showPw ? EyeOff : Eye} size={15} />
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading}
              style={{ padding: '13px', borderRadius: '10px', background: '#007aff', color: '#fff', border: 'none', fontWeight: 'bold', fontSize: '0.95rem', cursor: loading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '4px' }}>
              {loading ? <Icon icon={Loader} size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Icon icon={LogIn} size={16} />}
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
            <button type="button" onClick={() => { setMode('choose'); setError(''); }}
              style={{ background: 'none', border: 'none', color: theme.textSub, fontSize: '0.82rem', cursor: 'pointer', textDecoration: 'underline', marginTop: '2px' }}>
              ← Back
            </button>
          </form>
        )}

        {/* Google button container */}
        {mode === 'google' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            {loading && <div style={{ display: 'flex', gap: '8px', alignItems: 'center', color: theme.textSub, fontSize: '0.85rem' }}><Icon icon={Loader} size={16} style={{ animation: 'spin 1s linear infinite' }} />Signing in…</div>}
            <div ref={googleBtnRef} />
            <button type="button" onClick={() => { setMode('choose'); setError(''); }}
              style={{ background: 'none', border: 'none', color: theme.textSub, fontSize: '0.82rem', cursor: 'pointer', textDecoration: 'underline' }}>
              ← Back
            </button>
          </div>
        )}

        {mode !== 'request' && (
          <p style={{ textAlign: 'center', fontSize: '0.7rem', color: theme.textSub, marginTop: '20px', lineHeight: 1.5 }}>
            Access is by invitation only.{' '}
            <button onClick={() => { setMode('request'); setError(''); setRequestSent(false); }}
              style={{ background: 'none', border: 'none', color: theme.glossaryAccent, cursor: 'pointer', fontSize: '0.7rem', textDecoration: 'underline', padding: 0 }}>
              Request access
            </button>
          </p>
        )}

        {/* ── Request access form ── */}
        {mode === 'request' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '4px' }}>
            {requestSent ? (
              <div style={{ textAlign: 'center', padding: '20px 8px' }}>
                <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'rgba(34,197,94,0.12)', border: '1.5px solid rgba(34,197,94,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                  <Icon icon={Check} size={24} color="#22c55e" />
                </div>
                <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '8px', color: theme.text }}>Request sent!</div>
                <div style={{ fontSize: '0.8rem', color: theme.textSub, lineHeight: 1.6 }}>
                  Your request has been received. The administrator will review it and be in touch at <strong style={{ color: theme.text }}>{requestEmail}</strong>.
                </div>
                <button onClick={() => { setMode('choose'); setRequestEmail(''); setRequestName(''); setRequestMessage(''); setRequestSent(false); }}
                  style={{ marginTop: '18px', background: 'none', border: `1px solid ${theme.border}`, color: theme.textSub, fontSize: '0.8rem', cursor: 'pointer', padding: '7px 18px', borderRadius: '8px' }}>
                  ← Back to sign in
                </button>
              </div>
            ) : (
              <>
                <div style={{ textAlign: 'center', marginBottom: '4px' }}>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: theme.text, marginBottom: '4px' }}>Request Access</div>
                  <div style={{ fontSize: '0.72rem', color: theme.textSub, lineHeight: 1.5 }}>Tell us a little about yourself and we'll be in touch.</div>
                </div>
                <input placeholder="Your full name" aria-label="Your full name" value={requestName} onChange={e => setRequestName(e.target.value)} onFocus={applyFocusRing} onBlur={removeFocusRing}
                  style={{ padding: '11px 14px', borderRadius: '8px', border: `1.5px solid ${theme.border}`, background: theme.inputBg, color: theme.text, fontSize: '0.9rem', outline: 'none' }} />
                <input type="email" placeholder="Your email address" aria-label="Your email address" value={requestEmail} onChange={e => setRequestEmail(e.target.value)} onFocus={applyFocusRing} onBlur={removeFocusRing}
                  style={{ padding: '11px 14px', borderRadius: '8px', border: `1.5px solid ${theme.border}`, background: theme.inputBg, color: theme.text, fontSize: '0.9rem', outline: 'none' }} />
                <textarea placeholder="How did you hear about this project? What draws you to this work? (optional)" aria-label="How you heard about the project" value={requestMessage} onChange={e => setRequestMessage(e.target.value)} rows={3} onFocus={applyFocusRing} onBlur={removeFocusRing}
                  style={{ padding: '11px 14px', borderRadius: '8px', border: `1.5px solid ${theme.border}`, background: theme.inputBg, color: theme.text, fontSize: '0.85rem', outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
                <button disabled={!requestEmail || !requestName || sending}
                  onClick={sendRequest}
                  style={{ padding: '12px', borderRadius: '10px', background: theme.glossaryAccent, color: '#fff', border: 'none', fontWeight: 'bold', cursor: (!requestEmail || !requestName || sending) ? 'not-allowed' : 'pointer', opacity: (!requestEmail || !requestName || sending) ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '0.95rem' }}>
                  {sending ? <Icon icon={Loader} size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Icon icon={Mail} size={16} />}
                  {sending ? 'Sending…' : 'Send Request'}
                </button>
                <button onClick={() => { setMode('choose'); setError(''); setRequestEmail(''); setRequestName(''); setRequestMessage(''); setRequestSent(false); }}
                  style={{ background: 'none', border: 'none', color: theme.textSub, fontSize: '0.82rem', cursor: 'pointer', textDecoration: 'underline' }}>
                  ← Back
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {isDevEnvironment && (
        <div style={{
          marginTop: '20px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          padding: '5px 14px',
          borderRadius: '999px',
          background: 'rgba(220, 38, 38, 0.12)',
          border: '1px solid rgba(220, 38, 38, 0.34)',
          color: '#dc2626',
          fontSize: '0.68rem',
          fontWeight: 800,
          letterSpacing: '0.09em',
          textTransform: 'uppercase',
        }}>
          <Icon icon={AlertTriangle} size={11} color="#dc2626" />
          Dev Environment
        </div>
      )}
    </div>
  );
}
